import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Award, Users, BookOpen, Trophy, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import AdminLayout from '../../components/admin/AdminLayout';

const CoreValuesManagement = () => {
  const [coreValues, setCoreValues] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    icon_name: 'Award',
    title: '',
    description: '',
    order_index: 0,
    is_active: true,
  });

  const iconOptions = [
    { value: 'Award', label: 'Ödül', icon: <Award className="w-5 h-5" /> },
    { value: 'Users', label: 'Kullanıcılar', icon: <Users className="w-5 h-5" /> },
    { value: 'BookOpen', label: 'Kitap', icon: <BookOpen className="w-5 h-5" /> },
    { value: 'Trophy', label: 'Kupa', icon: <Trophy className="w-5 h-5" /> },
  ];

  useEffect(() => {
    fetchCoreValues();
  }, []);

  const fetchCoreValues = async () => {
    try {
      const response = await fetch('http://localhost/api/core-values.php', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const text = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { error: 'Sunucu hatası' };
        }
        toast.error(errorData.error || 'Neden Biz? yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      
      // is_active değerlerini normalize et
      const normalizedData = data.map(item => ({
        ...item,
        is_active: item.is_active === 1 || item.is_active === '1' || item.is_active === true ? 1 : 0,
      }));
      
      setCoreValues(normalizedData);
    } catch (error) {
      console.error('Error fetching Neden Biz?:', error);
      toast.error('Neden Biz? yüklenirken hata oluştu');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingItem 
        ? `http://localhost/api/core-values.php?id=${editingItem.id}`
        : 'http://localhost/api/core-values.php';
      
      const method = editingItem ? 'PUT' : 'POST';
      
      const dataToSend = {
        icon_name: formData.icon_name,
        title: formData.title,
        description: formData.description,
        order_index: parseInt(formData.order_index) || 0,
        is_active: formData.is_active ? 1 : 0,
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(dataToSend),
      });
      
      const text = await response.text();
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { error: 'Sunucu hatası' };
        }
        toast.error(errorData.error || 'Kayıt yapılırken hata oluştu');
        return;
      }
      
      await fetchCoreValues();
      setIsModalOpen(false);
      setEditingItem(null);
      setFormData({
        icon_name: 'Award',
        title: '',
        description: '',
        order_index: 0,
        is_active: true,
      });
      toast.success(editingItem ? 'Değer başarıyla güncellendi' : 'Değer başarıyla oluşturuldu');
    } catch (error) {
      console.error('Error saving core value:', error);
      toast.error('Kayıt yapılırken hata oluştu');
    }
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;
    
    try {
      const response = await fetch(`http://localhost/api/core-values.php?id=${deleteItemId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const text = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { error: 'Sunucu hatası' };
        }
        toast.error(errorData.error || 'Silme işlemi başarısız');
        return;
      }
      
      await fetchCoreValues();
      toast.success('Değer başarıyla silindi');
      setIsDeleteModalOpen(false);
      setDeleteItemId(null);
    } catch (error) {
      console.error('Error deleting core value:', error);
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleToggleActive = async (item) => {
    // Mevcut durumu kontrol et - farklı formatları destekle
    const currentActive = item.is_active === 1 || item.is_active === '1' || item.is_active === true;
    const newActiveValue = currentActive ? 0 : 1;
    
    // State'i anında güncelle (optimistic update)
    setCoreValues(prev => prev.map(cv => 
      cv.id === item.id 
        ? { ...cv, is_active: newActiveValue }
        : cv
    ));
    
    try {
      const dataToSend = {
        icon_name: item.icon_name,
        title: item.title,
        description: item.description,
        order_index: item.order_index,
        is_active: newActiveValue,
      };
      
      const response = await fetch(`http://localhost/api/core-values.php?id=${item.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(dataToSend),
      });
      
      const text = await response.text();
      
      if (!response.ok) {
        // Hata durumunda state'i geri al
        setCoreValues(prev => prev.map(cv => 
          cv.id === item.id 
            ? { ...cv, is_active: currentActive ? 1 : 0 }
            : cv
        ));
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { error: 'Sunucu hatası' };
        }
        toast.error(errorData.error || 'Durum değiştirilemedi');
        return;
      }
      
      // Başarılı - state'i güncelle ve verileri yeniden çek
      setCoreValues(prev => prev.map(cv => 
        cv.id === item.id 
          ? { ...cv, is_active: newActiveValue }
          : cv
      ));
      
      await fetchCoreValues();
      toast.success('Durum başarıyla güncellendi');
    } catch (error) {
      // Hata durumunda state'i geri al
      setCoreValues(prev => prev.map(cv => 
        cv.id === item.id 
          ? { ...cv, is_active: currentActive ? 1 : 0 }
          : cv
      ));
      console.error('Error toggling active:', error);
      toast.error('Durum değiştirilemedi');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      icon_name: item.icon_name || 'Award',
      title: item.title || '',
      description: item.description || '',
      order_index: item.order_index || 0,
      is_active: item.is_active === 1 || item.is_active === true,
    });
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      icon_name: 'Award',
      title: '',
      description: '',
      order_index: 0,
      is_active: true,
    });
    setIsModalOpen(true);
  };

  const getIcon = (iconName) => {
    const icons = {
      Award: <Award className="w-6 h-6" />,
      Users: <Users className="w-6 h-6" />,
      BookOpen: <BookOpen className="w-6 h-6" />,
      Trophy: <Trophy className="w-6 h-6" />,
    };
    return icons[iconName] || <Award className="w-6 h-6" />;
  };

  return (
    <AdminLayout
      addButton={
        <Button onClick={handleAdd} className="bg-[#C0A062] hover:bg-[#9a7d4a] text-white flex items-center gap-2">
          <Plus size={20} />
          Yeni Değer Ekle
        </Button>
      }
    >
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <div className="inline-block min-w-full align-middle sm:px-0 px-3">
              <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Icon
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Başlık
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Açıklama
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sıra
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {coreValues.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    Henüz değer eklenmemiş
                  </td>
                </tr>
              ) : (
                coreValues.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#C0A062]/10">
                        {getIcon(item.icon_name)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{item.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 line-clamp-2">{item.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.order_index}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(item)}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          item.is_active === 1 || item.is_active === true
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {item.is_active === 1 || item.is_active === true ? 'Aktif' : 'Pasif'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-[#C0A062] hover:text-[#9a7d4a]"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
            </div>
          </div>
        </div>

        {/* Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full mx-2 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? 'Değer Düzenle' : 'Yeni Değer Ekle'}
              </DialogTitle>
              <DialogDescription>
                {editingItem ? 'Değer bilgilerini düzenleyin' : 'Yeni bir değer ekleyin'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon
                </label>
                <Select
                  value={formData.icon_name}
                  onValueChange={(value) => handleSelectChange('icon_name', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Icon seçin">
                      <div className="flex items-center gap-2">
                        {getIcon(formData.icon_name)}
                        <span>{iconOptions.find(opt => opt.value === formData.icon_name)?.label || formData.icon_name}</span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          {option.icon}
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Başlık *
                </label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Başlık girin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama *
                </label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  placeholder="Açıklama girin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sıra
                </label>
                <Input
                  name="order_index"
                  type="number"
                  value={formData.order_index}
                  onChange={handleInputChange}
                  placeholder="0"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-[#C0A062] focus:ring-[#C0A062]"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Aktif
                </label>
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingItem(null);
                  }}
                >
                  İptal
                </Button>
                <Button type="submit" className="bg-[#C0A062] hover:bg-[#9a7d4a]">
                  {editingItem ? 'Güncelle' : 'Oluştur'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
          <DialogContent className="max-w-md w-[95vw] sm:w-full mx-2 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>Silme Onayı</DialogTitle>
              <DialogDescription>
                Bu işlem geri alınamaz.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-700">Bu değeri silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeleteItemId(null);
                }}
              >
                İptal
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={confirmDelete}
              >
                Sil
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default CoreValuesManagement;
